/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useAppSelector } from '../../../lib/redux/hooks';
import { addFilterModal } from '../../../lib/redux/reducers/filter.slice';
import { useCategoryChips } from '../../../hooks/category/useCategoryChips';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import CategoryChip from './CategoryChip';

// Category chip bar (category discovery P0): the always-visible, one-tap
// category control. One component, two placements:
//   list - in flow under the search row, sticky under the 72px header
//   map  - absolute over the mobile full-screen map, under the keyword bar
// Single-select for speed (the filter sheet is the multi-select surface);
// both write the same redux filterModal.categories, so bar and sheet can
// never fight. Tap = instant filter - no Apply.
type Props = {
  placement: 'list' | 'map';
};

const CategoryChipBar: React.FC<Props> = ({ placement }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const filterFromModal = useAppSelector((state) => state.filter.filterModal);
  const { options, selectedIds, loading } = useCategoryChips();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const allActive = selectedIds.length === 0;
  const isActive = (value: string) => selectedIds.includes(value);

  const selectCategory = (option?: { value: string; label: string }) => {
    // Tap the active chip (or its ×, or All) -> back to All = empty array =
    // no category filter. NOTE: [] deliberately, not the full category list -
    // the full-list form silently hid uncategorized listings.
    const next = !option || isActive(option.value) ? [] : [option];
    dispatch(
      addFilterModal({
        filterModal: { ...filterFromModal, categories: next }
      })
    );
  };

  // Sticky "stuck" hairline (list placement): a 1px sentinel right above the
  // bar leaves the viewport exactly when the bar pins under the header.
  useEffect(() => {
    if (placement !== 'list' || !sentinelRef.current) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      rootMargin: '-72px 0px 0px 0px',
      threshold: 0
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [placement]);

  // Desktop scroll affordance: step arrows appear only when the row actually
  // overflows (touch devices keep native momentum + the peeking chip).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const measure = () => setOverflowing(scroller.scrollWidth > scroller.clientWidth + 4);
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(scroller);
    return () => resizeObserver.disconnect();
  }, [options.length, loading]);

  const step = (direction: 1 | -1) => {
    scrollerRef.current?.scrollBy({
      left: direction * Math.max(160, (scrollerRef.current?.clientWidth ?? 320) * 0.7),
      behavior: 'smooth'
    });
  };

  // Roving arrow-key focus keeps the radiogroup to ONE tab stop (a11y spec).
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    const chips = Array.from(
      scrollerRef.current?.querySelectorAll<HTMLButtonElement>('button[role="radio"]') ?? []
    );
    const index = chips.indexOf(document.activeElement as HTMLButtonElement);
    if (index === -1) {
      return;
    }
    event.preventDefault();
    const next =
      chips[(index + (event.key === 'ArrowRight' ? 1 : -1) + chips.length) % chips.length];
    next?.focus();
    next?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  };

  // Reserved height (54px) in both placements: chips landing must never move
  // the map or the list under the user's finger.
  const skeleton = (
    <div className="flex items-center gap-2" aria-hidden="true">
      {[64, 158, 132, 110].map((width) => (
        <span
          key={width}
          className="h-9 shrink-0 animate-pulse rounded-full bg-gray-100"
          style={{ width }}
        />
      ))}
    </div>
  );

  // Over the map only the chips themselves catch the pointer - the bar strip
  // between them stays draggable map.
  const isMap = placement === 'map';
  const chipShadowClass = isMap ? 'shadow-[0_1px_3px_rgba(16,24,40,0.12)]' : '';

  const chips = (
    <>
      <CategoryChip
        isAll
        active={allActive}
        onSelect={() => selectCategory(undefined)}
        className={chipShadowClass}
      />
      {options.map((option) => (
        <CategoryChip
          key={option.value}
          option={option}
          active={isActive(option.value)}
          onSelect={() => selectCategory(option)}
          className={chipShadowClass}
        />
      ))}
    </>
  );

  const scroller = (
    <div
      ref={scrollerRef}
      role="radiogroup"
      aria-label={intl.formatMessage({
        id: TRANSLATED_CONSTANTS.CATEGORY.id,
        defaultMessage: TRANSLATED_CONSTANTS.CATEGORY.defaultMessage
      })}
      onKeyDown={onKeyDown}
      className={`no-scrollbar chipbar-fade flex items-center gap-2 overflow-x-auto pb-2.5 pt-2 ${
        isMap ? 'pointer-events-auto px-3' : 'px-3 md:px-12 lg:ps-[60px] lg:pe-8'
      }`}>
      {loading ? skeleton : chips}
    </div>
  );

  if (isMap) {
    // Mobile full-screen map: the keyword bar owns top-3 (12..56px); the chip
    // bar takes the next row. Everything below it drops by the bar's 54px
    // reserved height (toggle, search-this-area, zoom - see MapboxLe/map.css).
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[60px] z-[3]">{scroller}</div>
    );
  }

  return (
    <div className="relative -mx-3 md:-mx-12 lg:-ms-[60px] lg:-me-8">
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-px" />
      <div
        className={`group sticky top-[72px] z-[4] transition-shadow ${
          stuck ? 'bg-white shadow-[inset_0_-1px_0_#EAECF0]' : ''
        }`}>
        {scroller}
        {overflowing && (
          <>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => step(-1)}
              className="absolute start-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 md:group-hover:flex">
              ‹
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => step(1)}
              className="absolute end-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 md:group-hover:flex">
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryChipBar;
